/**
 * FileUploadField — pick a file from the user's device, upload it, show a
 * preview, with replace/remove actions.  Replaces the URL-paste-only
 * MediaUrlField wherever an editor would otherwise have to host an asset
 * elsewhere and paste a link.
 *
 * Props:
 *   - label        section label shown above the field
 *   - value        the current URL stored in the content payload
 *   - onChange     called with the new URL after upload, or '' on remove
 *   - kind         'image' | 'video' | 'logo' | 'other' — sent to backend so
 *                  it can categorise + later filter library by kind
 *   - accept       optional override for <input accept=> (e.g. 'image/*')
 *   - hint         small grey text shown next to the label
 *
 * The component is permissive about the current value:
 *   - Empty string → "no asset yet" empty state with a single Upload button
 *   - A URL        → preview thumbnail/video + Replace + Remove buttons
 */

import { useRef, useState } from 'react'
import { landingCmsApi } from '../landingCmsApi'

const ACCEPT_BY_KIND = {
  image: 'image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml',
  video: 'video/mp4,video/webm,video/quicktime',
  logo:  'image/svg+xml,image/png,image/jpeg,image/webp',
  other: '',
}

export default function FileUploadField({
  label,
  value,
  onChange,
  kind = 'image',
  accept,
  hint,
}) {
  const inputRef = useRef(null)
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState('')

  const acceptAttr = accept || ACCEPT_BY_KIND[kind] || ''

  const pickFile = () => inputRef.current?.click()

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''  // allow re-uploading the same filename later
    if (!file) return
    setError('')
    setUploading(true)
    setProgress(0)
    try {
      const data = await landingCmsApi.uploadMedia(file, kind, (pct) => setProgress(pct))
      if (data?.url) {
        // Defensive: strip any trailing slash before storing.  nginx serves
        // static files via /media/ — a trailing slash makes it interpret
        // the URL as a directory and return 403 Forbidden.
        const cleanUrl = String(data.url).replace(/\/+$/, '')
        onChange(cleanUrl)
      } else {
        setError('Upload returned no URL')
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Upload failed')
    } finally {
      setUploading(false)
      setProgress(0)
    }
  }

  const remove = () => {
    onChange('')
  }

  const showPreview = !!value && !uploading
  const isVideo = kind === 'video'

  return (
    <div className="mb-3">
      <div className="flex items-baseline justify-between">
        <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">{label}</label>
        {hint && <span className="text-[10px] text-gray-400 font-normal normal-case">{hint}</span>}
      </div>

      {/* Hidden native file input — triggered by the visible buttons */}
      <input
        ref={inputRef}
        type="file"
        accept={acceptAttr}
        onChange={handleFileChange}
        className="hidden"
      />

      {showPreview ? (
        <div className="flex items-start gap-3 bg-white border border-gray-200 rounded-lg p-3">
          <div className="flex-shrink-0">
            {isVideo ? (
              <video
                src={value}
                className="w-32 h-20 rounded-md border border-gray-200 bg-gray-50 object-cover"
                muted
              />
            ) : (
              // eslint-disable-next-line jsx-a11y/img-redundant-alt
              <img
                src={value}
                alt="preview"
                className="w-20 h-20 object-cover rounded-md border border-gray-200 bg-gray-50"
                onError={(e) => { e.currentTarget.style.opacity = '0.3' }}
              />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-700 truncate" title={value} dir="ltr">{value}</p>
            <div className="flex items-center gap-2 mt-2">
              <button
                type="button"
                onClick={pickFile}
                disabled={uploading}
                className="px-2.5 py-1 text-xs rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
              >
                Replace
              </button>
              <button
                type="button"
                onClick={remove}
                className="px-2.5 py-1 text-xs rounded-md border border-red-200 bg-white hover:bg-red-50 text-red-700"
              >
                Remove
              </button>
              <a
                href={value}
                target="_blank"
                rel="noopener noreferrer"
                className="px-2.5 py-1 text-xs rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50"
                title="Open in new tab"
              >
                Open
              </a>
            </div>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={pickFile}
          disabled={uploading}
          className={`w-full px-4 py-6 rounded-lg border-2 border-dashed text-sm transition-colors ${
            uploading
              ? 'border-cyan-300 bg-cyan-50/50 text-cyan-800'
              : 'border-gray-300 bg-white hover:bg-gray-50 hover:border-cyan-400 hover:text-cyan-700 text-gray-600'
          }`}
        >
          {uploading ? (
            <span className="inline-flex items-center gap-2">
              <span>Uploading… {progress}%</span>
              <span className="inline-block w-32 h-1 bg-cyan-100 rounded-full overflow-hidden align-middle">
                <span className="block h-1 bg-cyan-600 transition-all" style={{ width: `${progress}%` }} />
              </span>
            </span>
          ) : (
            <span>
              <span className="block font-medium">Click to upload {kind === 'video' ? 'a video' : 'an image'}</span>
              <span className="block text-[11px] text-gray-500 mt-1">
                {isVideo ? 'MP4, WebM, MOV up to 25 MB' : 'PNG, JPG, GIF, WebP, SVG up to 25 MB'}
              </span>
            </span>
          )}
        </button>
      )}

      {error && (
        <p className="text-xs text-red-700 mt-1.5">{error}</p>
      )}
    </div>
  )
}
